import UserEventHandler from '@sendbird/uikit-react/handlers/UserEventHandler';
import sbSelectors from '@sendbird/uikit-react/sendbirdSelectors';
import useSendbirdStateContext from '@sendbird/uikit-react/useSendbirdStateContext';
import clsx from 'clsx';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { isSupported as badgeIsSupported, set as badgeSet } from 'MobileBadge';
import { setUnreadCount as setUnreadCountAction } from '../actions';

const classes = {
    container: (count: number) =>
        clsx(
            'absolute',
            'pointer-events-none',
            'h-[19px]',
            count > 99 ? 'w-[26px]' : 'w-[19px]',
            'rounded-full',
            'border',
            'border-white',
            'overflow-hidden',
            'top-[calc(50%-18px)]',
            'sm:top-[calc(50%-23px)]',
            'max-sm:ltr:right-[79px]',
            'max-sm:rtl:left-[79px]',
            'ltr:sm:right-[82px]',
            'rtl:sm:left-[82px]',
            'rtl:sm:right-auto',
            'z-[110]',
        ),
    content: clsx(
        'pointer-events-none',
        'text-center',
        'text-2xs',
        'font-semibold',
        'bg-blue',
        'text-white',
        'h-full',
        'w-full',
    ),
};

export const UnreadCount = memo(() => {
    const [unreadCount, setUnreadCount] = useState(0);
    const dispatch = useDispatch();
    const sbContext = useSendbirdStateContext();

    const sdk = sbSelectors.getSdk(sbContext);

    const setBadgeCount = useCallback(async (count: number) => {
        // Set the mobile app badge count
        const badgeSupported = await badgeIsSupported();
        if (badgeSupported) {
            await badgeSet(count);
        }
    }, []);

    const messageChangeHandler = useMemo(
        () =>
            new UserEventHandler({
                onTotalUnreadMessageCountChanged: count => {
                    setUnreadCount(count.groupChannelCount);
                },
            }),
        [],
    );

    useEffect(() => {
        if (!sdk) return undefined;
        let cancelled = false;

        const fetchCount = () => {
            if (cancelled) return;
            if (!sdk.groupChannel) {
                // groupChannel may not be initialized yet on cold start; retry shortly
                setTimeout(fetchCount, 200);
                return;
            }
            // Explicit `: number` because Sendbird SDK types don't fully resolve under bundler moduleResolution.
            // Remove annotation if @sendbird/chat fixes its exports map.
            sdk.groupChannel.getTotalUnreadMessageCount().then((v: number) => {
                if (!cancelled) setUnreadCount(v);
            });
        };

        fetchCount();
        return () => {
            cancelled = true;
        };
    }, [sdk]);

    useEffect(() => {
        // getSdk() can return a partial SDK object that lacks user-event methods;
        // guard to avoid "addUserEventHandler is not a function" (SMARTLY-KHN)
        if (!sdk?.addUserEventHandler) return undefined;
        // update count as new messages are received
        sdk.addUserEventHandler('message_count', messageChangeHandler);

        return () => {
            sdk.removeUserEventHandler('message_count');
        };
    }, [messageChangeHandler, sdk]);

    useEffect(() => {
        // Keep the badge count and Redux store updated
        setBadgeCount(unreadCount);
        dispatch(setUnreadCountAction(unreadCount));
    }, [unreadCount, setBadgeCount, dispatch]);

    return (
        unreadCount > 0 && (
            <div className={classes.container(unreadCount)}>
                <div className={classes.content}>{unreadCount > 99 ? '99+' : unreadCount}</div>
            </div>
        )
    );
});
